<script>
import { mapGetters, mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { email, required } from 'vuelidate/lib/validators'
import { fieldValidationsMixin } from '@/mixins/fieldValidationsMixin'
import { formatter } from '@/helpers/formatter'
import { Salutation } from '@/enums'
export default {
  mixins: [validationMixin, formatter, fieldValidationsMixin],
  props: {
    organization: { type: Object },
    tabName: {type: String}
  },
  validations: {
    email: { required, email },
    salutation: { required },
    firstName: { required },
    lastName: { required },
    billingData: {
      billingEmail: { required, email }
    }
  },
  data(vm) {
    return {
      submitProfileError: false,
      submitError: false,
      isDisplayingProfileNotification: false,
      isDisplayingNotification: false,
      hasBlurredUserProfile: false,
      salutations: [
        {
          value: Salutation.Sir,
          text: vm.$t('form.label.mr')
        },
        {
          value: Salutation.Madam,
          text: vm.$t('form.label.mrs')
        }
      ],
      salutation: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: vm.$store.getters.username,
      isCheckboxConfirmed: false,
      isAVVConfirmed: false,
      billingData: {
        billingEmail: '',
        billingCompanyName: ''
      },
      rootData: {},
      loading: false,
      backgroundColor: 'transparent',
      devStage: window.config.VUE_APP_STAGE !== 'PROD'
    }
  },
  computed: {
    ...mapGetters(['hasAVV']),
    ...mapState({
      userProfile: state => state.profile
    }),
    billingDataChanged() {
      return JSON.stringify(this.billingData) !== JSON.stringify(this.rootData)
    }
  },
  watch: {
    organization: {
      handler: async function(newValue) {
        try {
          this.billingData = await this.$organizationService.getBillingData(newValue.OrganizationId)
          this.rootData = JSON.parse(JSON.stringify(this.billingData))
        } catch (error) {
          console.error('Failed to fetch billing data:', error)
        }
      },
      immediate: true
    }
  },
  created() {
    this.activeTab = this.tabName
  },
  methods: {
    async submitOrganizationUpdate() {
      this.loading = true
      if (this.$v.billingData.$invalid) {
        this.$notify(this.$t('form.billing.data.error'),{
          type: 'error'
        })
        this.loading = false
      } else {
        this.$organizationService.setBillingData(this.organization.OrganizationId, this.billingData)
        .then(() => {
          this.$notify(this.$t('form.billing.data.success'), {
            type: 'success'
          })
          this.$emit('submitted', true)
        })
        .catch((error) => {
          this.$notify(this.$t('form.billing.data.error'),{
            type: 'error'
          })
          console.error('Failed to update billing data:', error)
        })
        .finally(() => {
          this.loading = false
        })
      }
    },
    acceptAVV(value) {
      this.isAVVConfirmed = value
    }
  }
}
</script>
<template>
  <v-card
    flat
    tile
    color="transparent"
    class="base-data-form">
    <v-card-text>
      <div
        v-if="!organization.IsAdmin"
        class="mt-4">
        <v-icon>mdi-information-outline</v-icon>
        {{$t('form.admin.restricted')}}
      </div>
      <v-row align="center" class="mt-4">
        <v-col
          v-if="devStage"
          cols="1"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-organization-id"
            v-model="organization.OrganizationId"
            disabled
            :label="$t('form.label.organization.id')"
            outlined
            dense />
        </v-col>
        <v-col
          :cols="devStage ? 3 : 4"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''" >
          <v-text-field
            id="organization-billing-address-apl-id"
            v-model="billingData.aplId"
            disabled
            :label="$t('form.label.customer.number')"
            outlined
            dense />
        </v-col>
        <v-col
          cols="8"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-company-name"
            v-model="billingData.billingCompanyName"
            :readonly="!organization.IsAdmin"
            :label="$t('form.label.company.name')"
            outlined
            dense />
        </v-col>
        <v-col
          cols="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-key-account"
            v-model="billingData.billingReceiver"
            :readonly="!organization.IsAdmin"
            :label="$t('form.label.company.contact')"
            outlined
            dense />
        </v-col>
        <v-col
          cols="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            v-if="$v"
            id="organization-billing-address-key-email"
            v-model="billingData.billingEmail"
            :readonly="!organization.IsAdmin"
            :error-messages="[
              ...fieldNeccessary($v.billingData.billingEmail, $v),
              ...emailInvalid($v.billingData.billingEmail, $v)
            ]"
            :label="$t('form.label.invoice.email')"
            outlined
            required
            dense
            @input="$v.billingData.billingEmail.$touch()" />
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-address"
            v-model="billingData.billingStreetAndNumber"
            :readonly="!organization.IsAdmin"
            :label="$t('form.label.address')"
            outlined
            dense />
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-adress-extension"
            v-model="billingData.billingAddressAddition"
            :readonly="!organization.IsAdmin"
            :label="$t('form.label.address.extension')"
            outlined
            dense />
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-zipCode"
            v-model="billingData.billingZipCode"
            :readonly="!organization.IsAdmin"
            :label="$t('form.label.zip.code')"
            outlined
            dense />
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-city"
            v-model="billingData.billingCity"
            :readonly="!organization.IsAdmin"
            :label="$t('form.label.city')"
            outlined
            dense />
        </v-col>
        <v-col
          cols="12"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-country"
            v-model="billingData.billingCountry"
            disabled
            :label="$t('form.label.country')"
            outlined
            dense
            filled>
            <v-tooltip slot="prepend" top>
              <template #activator="{ on }">
                <v-icon color="primary" v-on="on">mdi-alert-circle-outline</v-icon>
              </template>
              <span>{{ $t('form.label.tooltip.country') }}</span>
            </v-tooltip>
          </v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-taxId"
            v-model="billingData.billingTaxId"
            :readonly="!organization.IsAdmin"
            :label="$t('form.label.tax.id')"
            outlined
            dense />
        </v-col>
        <v-col
          cols="12"
          md="6"
          :class="$vuetify.breakpoint.mdAndDown ? 'py-1' : ''">
          <v-text-field
            id="organization-billing-address-phone"
            v-model="billingData.billingPhoneNumber"
            :readonly="!organization.IsAdmin"
            :label="$t('form.label.phone')"
            outlined
            dense />
        </v-col>
      </v-row>
      <v-checkbox
        v-if="organization.IsAdmin"
        id="b2bOnlyOrganization"
        v-model="isCheckboxConfirmed"
        :label="$t('form.trader')"
        required>
      </v-checkbox>
    </v-card-text>
    <v-card-actions class="justify-center">
      <v-btn
        v-if="organization.IsAdmin"
        id="save-organization-billing-data"
        :loading="loading"
        class="text-center px-8 cms_register"
        color="primary"
        depressed
        :disabled="(!billingDataChanged) || (!isCheckboxConfirmed || (!isAVVConfirmed && !userProfile.acceptAvvDateTime))"
        @click.stop="submitOrganizationUpdate">
        {{ $t('button.save.organization.address') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
